// Helpers for the platform-wide WhatsApp number, sourced from settings.

export const WHATSAPP_FALLBACK = '+971528163743';

export function formatWhatsappDigits(raw: string): string {
  return (raw ?? '').replace(/\D/g, '');
}

export function formatWhatsappDisplay(raw: string): string {
  // Best-effort pretty format for E.164 strings starting with +.
  const digits = formatWhatsappDigits(raw);
  if (!digits) return raw;
  // UAE pattern: +971 NN NNN NNNN. Anything else: split groups of 3-4.
  if (digits.startsWith('971') && digits.length === 12) {
    return `+971 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return `+${digits}`;
}

export function buildWhatsappHref(raw: string, prefill?: string): string {
  const digits = formatWhatsappDigits(raw);
  const base = `https://wa.me/${digits || formatWhatsappDigits(WHATSAPP_FALLBACK)}`;
  if (!prefill) return base;
  return `${base}?text=${encodeURIComponent(prefill)}`;
}

// ---------------------------------------------------------------------------
// Forcing the CONSUMER WhatsApp app when WhatsApp Business is also installed
// ---------------------------------------------------------------------------
// A plain wa.me link is an https URL, so the phone picks the handler. When both
// WhatsApp (com.whatsapp) and WhatsApp Business (com.whatsapp.w4b) are
// installed, that choice is the OS's, not ours, and it often lands on Business.
//
// ANDROID: solvable. Chrome supports the `intent:` scheme, where `package=`
// names the exact app that must handle it, so the Business app is not a
// candidate at all. `S.browser_fallback_url` is what Chrome opens when
// com.whatsapp is genuinely absent, so a Business-only or app-less phone still
// reaches the normal wa.me page instead of a dead link.
//
// IOS: NOT solvable from a website, and we deliberately do not pretend it is.
// Both apps claim the same `whatsapp://` scheme and the same wa.me universal
// link, and iOS gives a site no way to name which one wins - there is no
// documented consumer-only scheme that is safe to rely on. Sending iOS an
// unhandled scheme risks an error dialog or a dead tap, which is strictly worse
// than today. iOS therefore keeps the plain https link, unchanged.
//
// Everything non-Android keeps the exact current behaviour, so this can only
// help Android and can never regress anyone else.

const ANDROID_WHATSAPP_PACKAGE = 'com.whatsapp';

// ---------------------------------------------------------------------------
// Choosing between the two apps (admin only)
// ---------------------------------------------------------------------------
// Staff have both apps installed, so an admin WhatsApp link opened whichever
// one Android felt like. Those links are asked about now (Safras, 19 Sep 2026):
// every WhatsApp link in the admin shows a two-button sheet on Android, every
// time, and each button is pinned to one package so the answer is obeyed.
//
// Every time, not remembered: staff move between the two accounts through the
// day, so a saved answer would be wrong as often as it was right.
//
// A site cannot see which apps are installed, so both buttons are always shown.
// If the chosen one is absent, S.browser_fallback_url lands on the ordinary
// wa.me page rather than a dead tap.
//
// Customer-facing links are untouched and stay pinned to consumer WhatsApp.

export const WHATSAPP_APPS = [
  { key: 'consumer', pkg: ANDROID_WHATSAPP_PACKAGE, label: 'WhatsApp' },
  { key: 'business', pkg: 'com.whatsapp.w4b', label: 'WhatsApp Business' },
] as const;

export type WhatsappAppKey = (typeof WHATSAPP_APPS)[number]['key'];

/**
 * Pull the number and prefill back out of an ordinary wa.me URL.
 *
 * Call sites already build `https://wa.me/<digits>?text=<encoded>`, so taking
 * that as the input means none of them has to thread the number and the message
 * separately just to offer the choice. Anything unparseable returns null and the
 * caller falls back to opening the URL as-is.
 */
export function parseWhatsappHref(href: string): { digits: string; prefill?: string } | null {
  try {
    const url = new URL(href);
    if (!/(^|\.)wa\.me$/i.test(url.hostname)) return null;
    const digits = url.pathname.replace(/\D/g, '');
    const text = url.searchParams.get('text');
    return { digits, prefill: text ?? undefined };
  } catch {
    return null;
  }
}

/** The same intent URL as below, pinned to a named package. */
export function buildWhatsappIntentHrefForPackage(
  pkg: string,
  raw: string,
  prefill?: string,
): string {
  const digits = formatWhatsappDigits(raw) || formatWhatsappDigits(WHATSAPP_FALLBACK);
  const query = `phone=${digits}${prefill ? `&text=${encodeURIComponent(prefill)}` : ''}`;
  const fallback = encodeURIComponent(buildWhatsappHref(raw, prefill));
  return (
    `intent://api.whatsapp.com/send?${query}#Intent;scheme=https;` +
    `package=${pkg};S.browser_fallback_url=${fallback};end`
  );
}

/** True for Android phones/tablets only. */
export function isAndroid(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  return /android/i.test(ua);
}

/**
 * Android `intent:` URL pinned to the consumer WhatsApp package.
 *
 * The host and path MUST be the real destination. Android parses
 * `intent://X/y` by taking X as the HOST, then rebuilds the data URI using
 * `scheme=`. So the host has to be api.whatsapp.com, giving
 * `https://api.whatsapp.com/send?phone=...` - a URI WhatsApp's own intent
 * filter matches. Writing `intent://send?...` instead yields `https://send?...`
 * (host "send"), which matches no filter and silently falls through.
 *
 * api.whatsapp.com rather than wa.me because wa.me is a redirector, and Chrome
 * refuses to launch an intent that arrives via a redirect.
 */
export function buildWhatsappIntentHref(raw: string, prefill?: string): string {
  // Chrome strips browser_fallback_url before handing the intent to the app.
  return buildWhatsappIntentHrefForPackage(ANDROID_WHATSAPP_PACKAGE, raw, prefill);
}

/**
 * The href to actually use on this device: package-pinned on Android, the
 * ordinary https link everywhere else.
 *
 * Callers must render the plain https href for the FIRST paint and only swap in
 * the intent URL once mounted on the client. Server and client disagree about
 * the user agent otherwise, which is a hydration mismatch, and an `intent:` URL
 * baked into static HTML would be served to iOS and desktop too.
 */
export function resolveWhatsappHref(raw: string, prefill?: string, userAgent?: string): string {
  return isAndroid(userAgent)
    ? buildWhatsappIntentHref(raw, prefill)
    : buildWhatsappHref(raw, prefill);
}
